/**
 * Settings → AI → Connect an AI app (MCP): how to connect Claude (or another agent), the connections you gave,
 * revoking them, connection links for agents without sign-in, and what agents did.
 */
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bot, Copy, Link2, ShieldOff } from "lucide-react";
import { formatTimestamp } from "@brainlog/shared";
import { errorMessage, get, post } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface AgentsInfo {
  mcpUrl: string;
  spaces: { id: string; name: string; kind: string; encrypted: boolean; externalProcessingDisabled: boolean }[];
  grants: { id: string; name: string; kind: "oauth" | "link"; workspaceIds: string[] | null; readOnly: boolean; createdAt: string; lastUsedAt: string | null; revokedAt: string | null }[];
  actions: { id: string; grantId: string; tool: string; workspaceId: string | null; summary: string; createdAt: string }[];
}

const copy = async (text: string, what: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied.`);
  } catch {
    toast.error("Copying was refused by the browser: select the text and copy it.");
  }
};

/** Which notebooks and projects a connection may use (all of them, or a chosen list). */
export function SpacePicker({ spaces, value, onChange }: { spaces: AgentsInfo["spaces"]; value: string[] | null; onChange(v: string[] | null): void }) {
  const usable = spaces.filter((s) => !s.encrypted);
  return (
    <fieldset className="space-y-1 text-sm">
      <legend className="sr-only">Notebooks and projects it may use</legend>
      <label className="flex items-center gap-2">
        <input type="radio" checked={value === null} onChange={() => onChange(null)} /> All my notebooks and projects (also future ones)
      </label>
      <label className="flex items-center gap-2">
        <input type="radio" checked={value !== null} onChange={() => onChange([])} /> Only these:
      </label>
      {value !== null && (
        <ul className="ml-6 space-y-0.5">
          {usable.map((s) => (
            <li key={s.id}>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={value.includes(s.id)} onChange={(e) => onChange(e.target.checked ? [...value, s.id] : value.filter((x) => x !== s.id))} />
                {s.name} <span className="text-xs text-muted-foreground">({s.kind}{s.externalProcessingDisabled ? ", outside services turned off, so it cannot be used" : ""})</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {spaces.some((s) => s.encrypted) && <p className="text-xs text-muted-foreground">End-to-end encrypted notebooks and projects are never available to AI apps.</p>}
    </fieldset>
  );
}

export function AgentsSettings() {
  const qc = useQueryClient();
  const me = useMe();
  const q = useQuery({ queryKey: ["agents"], queryFn: () => get<AgentsInfo>("/api/me/agents") });
  const [name, setName] = useState("My AI app");
  const [spaces, setSpaces] = useState<string[] | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [created, setCreated] = useState<{ url: string } | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [filter, setFilter] = useState<string>("");
  const names = useMemo(() => new Map((q.data?.grants ?? []).map((g) => [g.id, g.name])), [q.data]);
  const spaceName = useMemo(() => new Map((q.data?.spaces ?? []).map((s) => [s.id, s.name])), [q.data]);

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.isError || !q.data) return <p className="text-sm text-destructive">{errorMessage(q.error)}</p>;
  const d = q.data;
  const local = /^https?:\/\/(127\.0\.0\.1|localhost)/.test(d.mcpUrl);
  const active = d.grants.filter((g) => !g.revokedAt);
  const revoked = d.grants.filter((g) => g.revokedAt);
  const tz = me.data?.timezone;

  return (
    <div className="space-y-5 text-sm">
      <section aria-labelledby="agents-connect" className="space-y-2">
        <h3 id="agents-connect" className="font-medium">
          Connect an AI app
        </h3>
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-2">
          <code className="min-w-0 flex-1 break-all font-semibold" data-testid="mcp-url">
            {d.mcpUrl}
          </code>
          <Button size="sm" variant="outline" onClick={() => void copy(d.mcpUrl, "Address")}>
            <Copy /> Copy
          </Button>
        </div>
        {local && <p className="text-xs text-warning">This address only works on this computer (for example for Claude Code). Claude on the web reaches BrainLog once it runs at its public address.</p>}
        <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
          <li>
            <strong className="text-foreground">Claude (web and desktop):</strong> Settings → Connectors → <em>Add custom connector</em>, paste the address, click <em>Connect</em>, then sign in here and choose what
            it may use.
          </li>
          <li>
            <strong className="text-foreground">Other AI apps</strong> that cannot sign in: create a connection link below.
          </li>
          <li>
            <details>
              <summary className="cursor-pointer">Claude Code (for developers)</summary>
              <p className="mt-1">
                Run <code className="text-xs">claude mcp add --transport http brainlog {d.mcpUrl}</code>, then run <code className="text-xs">/mcp</code> and choose “Authenticate”.
              </p>
            </details>
          </li>
        </ul>
      </section>

      <section aria-labelledby="agents-list" className="space-y-2">
        <h3 id="agents-list" className="font-medium">
          Connected AI apps
        </h3>
        {active.length === 0 ? (
          <p className="text-muted-foreground">None yet.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {active.map((g) => (
              <li key={g.id} className="flex flex-wrap items-center gap-2 px-3 py-2" data-testid="agent-grant">
                <Bot className="size-4 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1">
                  <strong>{g.name}</strong> <Badge variant="outline">{g.kind === "oauth" ? "Signed in" : "Link"}</Badge> {g.readOnly && <Badge variant="secondary">Read only</Badge>}
                  <span className="block text-xs text-muted-foreground">
                    {g.workspaceIds ? g.workspaceIds.map((w) => spaceName.get(w) ?? "a deleted notebook or project").join(", ") || "no notebook or project" : "All notebooks and projects"} · since {formatTimestamp(g.createdAt, tz)} ·{" "}
                    {g.lastUsedAt ? `last used ${formatTimestamp(g.lastUsedAt, tz)}` : "not used yet"}
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    if (!window.confirm(`Disconnect “${g.name}”? It stops working at once.`)) return;
                    try {
                      await post(`/api/me/agents/${g.id}/revoke`, {});
                      toast.success("Disconnected.");
                      await qc.invalidateQueries({ queryKey: ["agents"] });
                    } catch (e) {
                      toast.error(errorMessage(e));
                    }
                  }}
                >
                  <ShieldOff /> Disconnect
                </Button>
              </li>
            ))}
          </ul>
        )}
        {revoked.length > 0 && (
          <button type="button" className="text-xs text-muted-foreground hover:underline" onClick={() => setShowRevoked((v) => !v)}>
            {showRevoked ? "Hide" : "Show"} {revoked.length} disconnected
          </button>
        )}
        {showRevoked && (
          <ul className="text-xs text-muted-foreground">
            {revoked.map((g) => (
              <li key={g.id}>
                {g.name} · disconnected {formatTimestamp(g.revokedAt, tz)}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="agents-link" className="space-y-2">
        <h3 id="agents-link" className="font-medium">
          Connection link
        </h3>
        <p className="text-xs text-muted-foreground">For AI apps that cannot sign in. Anyone with this link can act as you where you allow it. If it leaks, disconnect it above.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="agent-name">Name</Label>
            <Input id="agent-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
            <label className="mt-2 flex items-center gap-2">
              <input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} /> Read only (it cannot change anything)
            </label>
          </div>
          <SpacePicker spaces={d.spaces} value={spaces} onChange={setSpaces} />
        </div>
        <Button
          size="sm"
          disabled={!name.trim() || (spaces !== null && spaces.length === 0)}
          onClick={async () => {
            try {
              const r = await post<{ url: string }>("/api/me/agents/links", { name: name.trim(), workspaceIds: spaces, readOnly });
              setCreated({ url: r.url });
              await qc.invalidateQueries({ queryKey: ["agents"] });
            } catch (e) {
              toast.error(errorMessage(e));
            }
          }}
        >
          <Link2 /> Create a connection link
        </Button>
        {created && (
          <div className="space-y-1 rounded-md border border-warning/50 bg-warning/10 p-2">
            <p className="text-xs">Shown only once. Copy it now:</p>
            <div className="flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 break-all text-xs" data-testid="mcp-link">
                {created.url}
              </code>
              <Button size="sm" variant="outline" onClick={() => void copy(created.url, "Link")}>
                <Copy /> Copy
              </Button>
            </div>
          </div>
        )}
      </section>

      <section aria-labelledby="agents-activity" className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 id="agents-activity" className="flex-1 font-medium">
            What AI apps did
          </h3>
          {d.grants.length > 1 && (
            <select aria-label="AI app" className="rounded-md border bg-background px-2 py-1 text-xs" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="">All AI apps</option>
              {d.grants.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          )}
        </div>
        {d.actions.length === 0 ? (
          <p className="text-muted-foreground">Nothing yet.</p>
        ) : (
          <ol className="divide-y rounded-md border text-xs" aria-label="Agent activity, newest first">
            {d.actions
              .filter((a) => !filter || a.grantId === filter)
              .map((a) => (
                <li key={a.id} className="flex flex-wrap gap-x-2 px-3 py-1.5">
                  <span className="w-36 shrink-0 text-muted-foreground">{formatTimestamp(a.createdAt, tz)}</span>
                  <span className="font-medium">{names.get(a.grantId)}</span>
                  <span className="min-w-0 flex-1">{a.summary}</span>
                  {a.workspaceId && <span className="text-muted-foreground">{spaceName.get(a.workspaceId)}</span>}
                </li>
              ))}
          </ol>
        )}
      </section>
    </div>
  );
}
